import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/players/';
const TEAMS_API_URL = '/teams/';

const positionOptions = [
  { value: 'GK', label: 'Goalkeeper' },
  { value: 'DEF', label: 'Defender' },
  { value: 'MID', label: 'Midfielder' },
  { value: 'FWD', label: 'Forward' },
];

const PlayerForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    name: '',
    country: '',
    position: '',
    is_international: false,
  });

  useEffect(() => {
    axios.get(TEAMS_API_URL).then(res => setTeams(res.data)).catch(() => setTeams([]));
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          name: res.data.name || '',
          country: res.data.country ? res.data.country.id : '',
          position: res.data.position || '',
          is_international: !!res.data.is_international,
        });
      }).catch(() => setError('Failed to load player')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, country: form.country || null };
      if (isEdit) {
        await axios.put(API_URL + id + '/', payload);
        setSuccess('Player updated successfully');
      } else {
        await axios.post(API_URL, payload);
        setSuccess('Player created successfully');
        setForm({ name: '', country: '', position: '', is_international: false });
      }
      setTimeout(() => navigate('/football/players'), 1000);
    } catch (err: any) {
      setError('Failed to save player');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Player</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField select label="Country (Team)" name="country" value={form.country} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {teams.map(team => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}
          </TextField>
          <TextField select label="Position" name="position" value={form.position} onChange={handleChange} required fullWidth>
            {positionOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Checkbox checked={form.is_international} onChange={handleChange} name="is_international" />} label="International" />
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default PlayerForm; 